import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import {
  EstimateCreditsDetailResponse,
  estimateExportCredits,
  UniverseExportPayload,
} from "@/apis/exportTrackingApi";
import { ExportData } from "@/pages/Workspace/ExportModal/types";
import { CompanyExportParams } from "@/pages/Company/components/CompanyExport/types";
import { CreditsEstimateDetails } from "./CreditsEstimateDetails";

interface CreditsEstimateDialogProps {
  open: boolean;
  onClose: () => void;
  buildExportPayload: () =>
    | ExportData
    | CompanyExportParams
    | UniverseExportPayload;
  /** Called with each successful estimate and the payload that was priced. */
  onEstimate?: (
    result: EstimateCreditsDetailResponse,
    payload: ExportData | CompanyExportParams | UniverseExportPayload
  ) => void;
}

export const CreditsEstimateDialog: React.FC<CreditsEstimateDialogProps> = ({
  open,
  onClose,
  buildExportPayload,
  onEstimate,
}) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<EstimateCreditsDetailResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fetched, setFetched] = useState(false);

  const fetchCreditsDetails = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const payload = buildExportPayload();
      const response = await estimateExportCredits({ export_payload: payload });
      setData(response);
      onEstimate?.(response, payload);
    } catch (err) {
      console.error("Error fetching credits details:", err);
      setError("Failed to load credits estimate. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [buildExportPayload, onEstimate]);

  useEffect(() => {
    if (open && !fetched) {
      setFetched(true);
      fetchCreditsDetails();
    }

    if (!open) {
      setData(null);
      setError(null);
      setFetched(false);
      setLoading(false);
    }
  }, [open, fetched, fetchCreditsDetails]);

  const handleClose = () => {
    setData(null);
    setError(null);
    onClose();
  };

  const renderContent = () => {
    if (loading) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: 300,
          }}
        >
          <CircularProgress size={40} />
          <Box mt={2}>Loading credits estimate...</Box>
        </Box>
      );
    }

    if (error) {
      return (
        <>
          <Alert severity="error">{error}</Alert>
          <Box mt={2} display="flex" justifyContent="flex-end">
            <Button
              onClick={fetchCreditsDetails}
              color="primary"
              variant="contained"
            >
              Retry
            </Button>
          </Box>
        </>
      );
    }

    if (data) {
      return <CreditsEstimateDetails data={data} />;
    }

    return null;
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Credits Estimate Breakdown</DialogTitle>
      <DialogContent sx={{ minHeight: 400, minWidth: 500 }}>
        {renderContent()}
      </DialogContent>
      <DialogActions>
        <Button
          data-testid="credits-estimate-close-btn"
          onClick={handleClose}
          color="primary"
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
